'use client';

import { Separator } from '@/components/ui/separator';
import {
  CalendarIcon,
  ClockIcon,
  DollarSignIcon,
  TrophyIcon,
  UsersIcon,
  BarChart3Icon,
  AwardIcon,
  PencilIcon,
  Info,
  Users2Icon,
  MapPinIcon,
  Grid3X3,
  Hash,
  LayoutGrid,
  ListChecks,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { Club, Tournament } from '@/types';
import {
  CATEGORY_RANKING_TARGET,
  FixtureType,
  OverflowGroupClassification,
  TournamentFormat,
  type Schedule as TournamentScheduleBlock,
} from '@/types/tournament.model';
import type { PaymentType } from '@/types';
import { getPaymentTypeLabel } from '@/utils/payment-utils';
import { getGenderLabel } from '@/utils/gender-utils';
import { formatDateForUser } from '@/utils/formatters';
import DOMPurify from 'dompurify';
import { PriceBreakdown } from '../shared/PriceBreakdown';
import { TournamentCountdown } from '../TournamentCountdown';
import { CollapsibleCard } from './CollapsibleCard';
import { useSummaryCollapse } from './useSummaryCollapse';
import {
  isTournamentCancelled,
  isTournamentFinished,
} from '@/utils/tournament-status';
import { getTournamentAgeDisplayLines } from '@/utils/tournament-age';
import { getCourtsForSport } from '@/utils/club-utils';
import {
  getTournamentFormatLabel,
  isAmericanFormat,
  getAmericanFinalBracketLabel,
  getAmericanGroupFormatLabel,
} from '@/utils/tournament-format';
import { getFixtureTypeLabel } from '@/utils/fixture-utils';
import { getCategorySumLabel, type CategoryRef } from '@/utils/category-utils';

function scheduleBlockDisplayName(
  block: TournamentScheduleBlock,
  indexInClub: number,
): string {
  return block.name?.trim() || `Horario ${indexInClub + 1}`;
}

function scheduleBlockDateRangeLabel(
  block: TournamentScheduleBlock,
): string | null {
  const days = block.schedule ?? [];
  if (days.length === 0) return null;
  const sorted = [...days].sort((a, b) => a.date.localeCompare(b.date));
  const first = sorted[0].date;
  const last = sorted[sorted.length - 1].date;
  if (first === last) {
    return formatDateForUser(first);
  }
  return `${formatDateForUser(first)} – ${formatDateForUser(last)}`;
}

interface TournamentDetailsInfoCardProps {
  tournament: Tournament;
  clubs: Club[];
  sportUid?: string | null;
  onEditDescriptionClick: () => void;
  onEditPrizesClick: () => void;
  onEditDatesClick: () => void;
  /** Absent while the tournament already has a fixture: the deadline is frozen from then on. */
  onEditSignupDeadlineClick?: () => void;
  onEditPriceClick: () => void;
  onEditTeamsAllowedClick: () => void;
  onEditPlayersPerTeamClick: () => void;
  onEditCategoriesClick: () => void;
  onEditGenderClick: () => void;
  onEditAgeRestrictionClick: () => void;
  hasRegistrations: boolean;
  isEditMode: boolean;
  /**
   * The tournament's own `hasFixture`: true once a fixture exists (group stage and/or
   * knockouts). Locks editing overflow group classification.
   */
  hasFixture: boolean;
  onEditOverflowGroupClassificationClick?: () => void;
  onEditAmericanGroupFormatClick?: () => void;
  onEditMinSchedulesRequiredClick?: () => void;
  onEditCountsForCategoryRankingClick?: () => void;
  /** The sport's bands, needed to locate min/max positions and derive the sum. */
  eloCategories: CategoryRef[];
  /** Folded away while the tournament is in progress; the fixture takes the screen. */
  shouldCollapse?: boolean;
}

export function TournamentDetailsInfoCard({
  tournament,
  clubs,
  sportUid,
  onEditDescriptionClick,
  onEditPrizesClick,
  onEditDatesClick,
  onEditSignupDeadlineClick,
  onEditPriceClick,
  onEditTeamsAllowedClick,
  onEditPlayersPerTeamClick,
  onEditCategoriesClick,
  onEditGenderClick,
  onEditAgeRestrictionClick,
  hasRegistrations,
  isEditMode,
  hasFixture,
  onEditOverflowGroupClassificationClick,
  onEditAmericanGroupFormatClick,
  onEditMinSchedulesRequiredClick,
  onEditCountsForCategoryRankingClick,
  eloCategories,
  shouldCollapse = false,
}: TournamentDetailsInfoCardProps) {
  const [collapsed, setCollapsed] = useSummaryCollapse(shouldCollapse);

  const sanitizeHTML = (html: string) => {
    return {
      __html: DOMPurify.sanitize(html),
    };
  };

  const now = new Date();
  const startDate = new Date(tournament.startDate);
  const signupDeadlineDate = new Date(tournament.signupDeadlineDate);

  const canEditTeamsAllowed =
    isEditMode &&
    !isTournamentCancelled(tournament) &&
    (!hasRegistrations || (now < startDate && now <= signupDeadlineDate));

  const formatForLabel = tournament.format ?? TournamentFormat.SHORT;

  const showGroupsConfig =
    tournament.fixtureType === FixtureType.GROUPS_AND_KNOCKOUTS;

  const showOverflowRow =
    showGroupsConfig &&
    (tournament.format === TournamentFormat.SHORT ||
      tournament.format === undefined);

  // Americano tournaments do not use a minimum-schedules requirement.
  const showMinSchedulesRow = !isAmericanFormat(tournament.format);

  // The backend rejects editing this flag once the tournament is over, but the value
  // still has to be visible: it's what explains the points the tournament handed out.
  const canEditCountsForCategoryRanking = !isTournamentFinished(tournament);
  const countsForCategoryRanking = tournament.countsForCategoryRanking ?? true;

  const isAmerican = tournament.format === TournamentFormat.AMERICAN;

  const categorySumLabel = getCategorySumLabel(
    tournament.minCategory,
    tournament.maxCategory,
    tournament.forceSum,
    eloCategories,
  );

  const isOverflowExtra =
    tournament.overflowGroupClassification ===
    OverflowGroupClassification.EXTRA_CLASSIFIER;

  const canEditOverflowInModal =
    !isTournamentCancelled(tournament) &&
    showOverflowRow &&
    !hasFixture &&
    typeof onEditOverflowGroupClassificationClick === 'function';

  const showOverflowPencil = isEditMode && canEditOverflowInModal;

  const showAmericanGroupFormatPencil =
    isEditMode &&
    isAmerican &&
    showGroupsConfig &&
    !isTournamentCancelled(tournament) &&
    !hasFixture &&
    typeof onEditAmericanGroupFormatClick === 'function';

  return (
    <CollapsibleCard
      title="Detalles del torneo"
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      contentClassName="space-y-4"
    >
      <div>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="font-semibold">Descripción</h3>
          {isEditMode && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onEditDescriptionClick}
              className="h-6 w-6"
              aria-label="Editar descripción"
            >
              <PencilIcon className="h-4 w-4" />
            </Button>
          )}
        </div>
        <p
          className="text-gray-600"
          dangerouslySetInnerHTML={sanitizeHTML(tournament.description)}
        ></p>
      </div>

      <Separator />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* Sport */}
        <div className="flex items-start gap-3">
          <TrophyIcon className="mt-0.5 h-5 w-5 text-gray-500" />
          <div>
            <p className="font-medium">Deporte</p>
            <p className="text-sm text-gray-600">
              {tournament.sport?.name || 'No especificado'}
            </p>
          </div>
        </div>

        {/* Gender */}
        <div className="flex items-start gap-3">
          <Users2Icon className="mt-0.5 h-5 w-5 text-gray-500" />
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium">Género</p>
              {isEditMode && !hasRegistrations && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onEditGenderClick}
                  className="h-6 w-6"
                  aria-label="Editar género"
                >
                  <PencilIcon className="h-4 w-4" />
                </Button>
              )}
            </div>
            <p className="text-sm text-gray-600">
              {getGenderLabel(tournament.gender)}
            </p>
          </div>
        </div>

        {/* Dates */}
        <div className="flex items-start gap-3">
          <CalendarIcon className="mt-0.5 h-5 w-5 text-gray-500" />
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium">Fechas</p>
              {isEditMode && !hasRegistrations && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onEditDatesClick}
                  className="h-6 w-6"
                  aria-label="Editar fechas del torneo"
                >
                  <PencilIcon className="h-4 w-4" />
                </Button>
              )}
            </div>
            <p className="text-sm text-gray-600">
              {formatDateForUser(tournament.startDate)} -{' '}
              {formatDateForUser(tournament.endDate)}
            </p>
            <div className="mt-1 flex items-center gap-1">
              <p className="text-xs text-gray-500">
                Inscripciones hasta:{' '}
                {formatDateForUser(
                  tournament.signupDeadlineDate,
                  'dd MMM yyyy, HH:mm',
                )}{' '}
                hs
              </p>
              {isEditMode && onEditSignupDeadlineClick && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onEditSignupDeadlineClick}
                  className="h-6 w-6"
                  aria-label="Editar cierre de inscripción"
                >
                  <PencilIcon className="h-4 w-4" />
                </Button>
              )}
            </div>
            {!isTournamentCancelled(tournament) && (
              <div className="mt-2">
                <TournamentCountdown
                  signupDeadlineDate={tournament.signupDeadlineDate}
                  startDate={tournament.startDate}
                  endDate={tournament.endDate}
                  endsAt={tournament.endsAt}
                />
              </div>
            )}
          </div>
        </div>

        <div className="flex items-start gap-3">
          <DollarSignIcon className="mt-0.5 h-5 w-5 text-gray-500" />
          <div className="flex-1">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <p className="font-medium">Precio de inscripción por jugador</p>
                <button
                  onClick={() => {
                    // This will be handled by the PriceBreakdown component
                    const event = new CustomEvent('openOrganizerIncomeModal');
                    window.dispatchEvent(event);
                  }}
                  className="text-blue-600 transition-colors hover:text-blue-800"
                  aria-label="Ver detalle de ingreso del organizador"
                >
                  <Info className="h-4 w-4" />
                </button>
              </div>
              {isEditMode && !hasRegistrations && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onEditPriceClick}
                  className="h-6 w-6"
                  aria-label="Editar precio de inscripción"
                >
                  <PencilIcon className="h-4 w-4" />
                </Button>
              )}
            </div>
            <PriceBreakdown
              basePrice={tournament.price.value}
              commission={tournament.getmatchFee || 0}
              paymentMethod={getPaymentTypeLabel(
                tournament.paymentType as PaymentType,
              )}
              showPaymentMethod={true}
              showTitle={false}
              showOrganizerIncome={true}
              paymentType={tournament.paymentType as PaymentType}
            />
          </div>
        </div>

        <div className="flex items-start gap-3">
          <AwardIcon className="mt-0.5 h-5 w-5 text-gray-500" />
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium">Premios</p>
              {isEditMode && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onEditPrizesClick}
                  className="h-6 w-6"
                  aria-label="Editar premios"
                >
                  <PencilIcon className="h-4 w-4" />
                </Button>
              )}
            </div>
            <p
              className="text-sm text-gray-600"
              dangerouslySetInnerHTML={sanitizeHTML(tournament.prizes)}
            ></p>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <UsersIcon className="mt-0.5 h-5 w-5 text-gray-500" />
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium">Equipos</p>
              {canEditTeamsAllowed && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onEditTeamsAllowedClick}
                  className="h-6 w-6"
                  aria-label="Editar cantidad de equipos permitidos"
                >
                  <PencilIcon className="h-4 w-4" />
                </Button>
              )}
            </div>
            <p className="text-sm text-gray-600">
              Mínimo: {tournament.minTeamsAllowed} / Máximo:{' '}
              {tournament.maxTeamsAllowed}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Jugadores por equipo: {tournament.playersPerTeam}
              {isEditMode && !hasRegistrations && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onEditPlayersPerTeamClick}
                  className="ml-1 h-6 w-6"
                  aria-label="Editar jugadores por equipo"
                >
                  <PencilIcon className="h-4 w-4" />
                </Button>
              )}
            </p>
            <div className="mt-2 border-t border-gray-100 pt-2">
              <div className="flex items-center gap-2">
                <p className="text-xs font-medium text-gray-500">
                  Restricción de edad
                </p>
                {isEditMode && !hasRegistrations && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onEditAgeRestrictionClick}
                    className="h-6 w-6"
                    aria-label="Editar restricción de edad"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <div className="mt-1 space-y-0.5 text-sm text-gray-600">
                {getTournamentAgeDisplayLines(
                  tournament.minAge,
                  tournament.maxAge,
                ).map((line, i) => (
                  <p key={i}>{line}</p>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <BarChart3Icon className="mt-0.5 h-5 w-5 text-gray-500" />
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium">
                {tournament.minCategory.id === tournament.maxCategory.id
                  ? 'Categoría'
                  : 'Categorías'}
              </p>
              {isEditMode && !hasRegistrations && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onEditCategoriesClick}
                  className="h-6 w-6"
                  aria-label="Editar categorías"
                >
                  <PencilIcon className="h-4 w-4" />
                </Button>
              )}
            </div>
            <p className="text-sm text-gray-600">
              {categorySumLabel ??
                (tournament.minCategory.id === tournament.maxCategory.id
                  ? tournament.minCategory.name
                  : `${tournament.minCategory.name} a ${tournament.maxCategory.name}`)}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Elo mínimo: {tournament.minCategory.minimumElo}
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <ClockIcon className="mt-0.5 h-5 w-5 text-gray-500" />
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium">Duración de partidos</p>
            </div>
            <p className="text-sm text-gray-600">
              {tournament.matchDuration} minutos
            </p>
          </div>
        </div>

        {showMinSchedulesRow && (
          <div className="flex items-start gap-3">
            <Hash className="mt-0.5 h-5 w-5 text-gray-500" />
            <div>
              <div className="flex items-center gap-2">
                <p className="font-medium">Disponibilidad mínima</p>
                {isEditMode &&
                  typeof onEditMinSchedulesRequiredClick === 'function' && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={onEditMinSchedulesRequiredClick}
                      className="h-6 w-6"
                      aria-label="Editar mínimo de horarios por jugador"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </Button>
                  )}
              </div>
              <p className="text-sm text-gray-600">
                {tournament.minSchedulesRequired ?? 1}{' '}
                {(tournament.minSchedulesRequired ?? 1) === 1
                  ? 'horario'
                  : 'horarios'}{' '}
                por jugador
              </p>
            </div>
          </div>
        )}

        <div className="flex items-start gap-3">
          <TrophyIcon className="mt-0.5 h-5 w-5 text-gray-500" />
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium">Ranking de categoría</p>
              {isEditMode &&
                canEditCountsForCategoryRanking &&
                typeof onEditCountsForCategoryRankingClick === 'function' && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onEditCountsForCategoryRankingClick}
                    className="h-6 w-6"
                    aria-label="Editar si el torneo suma al ranking de categoría"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </Button>
                )}
            </div>
            <p className="text-sm text-gray-600">
              {!countsForCategoryRanking
                ? 'No suma puntos'
                : tournament.categoryRankingTarget ===
                    CATEGORY_RANKING_TARGET.FIXED_CATEGORY
                  ? `Suma puntos · ${tournament.categoryRankingCategory?.name ?? 'categoría fija'}`
                  : 'Suma puntos · categoría del jugador'}
            </p>
          </div>
        </div>
      </div>

      <Separator />

      <div>
        <h3 className="mb-3 font-semibold">
          Formato y configuración de grupos
        </h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="flex items-start gap-3">
            <LayoutGrid className="mt-0.5 h-5 w-5 shrink-0 text-gray-500" />
            <div>
              <p className="font-medium">Tipo de torneo</p>
              <p className="text-sm text-gray-600">
                {getTournamentFormatLabel(formatForLabel)}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Grid3X3 className="mt-0.5 h-5 w-5 shrink-0 text-gray-500" />
            <div>
              <p className="font-medium">Estructura del fixture</p>
              <p className="text-sm text-gray-600">
                {getFixtureTypeLabel(tournament.fixtureType)}
              </p>
            </div>
          </div>

          {showGroupsConfig && (
            <>
              <div className="flex items-start gap-3">
                <UsersIcon className="mt-0.5 h-5 w-5 shrink-0 text-gray-500" />
                <div>
                  <p className="font-medium">Equipos por grupo</p>
                  <p className="text-sm text-gray-600">
                    {tournament.teamsPerGroups}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <UsersIcon className="mt-0.5 h-5 w-5 shrink-0 text-gray-500" />
                <div>
                  <p className="font-medium">Clasificados por grupo</p>
                  <p className="text-sm text-gray-600">
                    {tournament.classifiedPerGroups}
                  </p>
                </div>
              </div>
            </>
          )}

          {isAmerican && (
            <div className="flex items-start gap-3">
              <TrophyIcon className="mt-0.5 h-5 w-5 shrink-0 text-gray-500" />
              <div>
                <p className="font-medium">Cuadro final</p>
                <p className="text-sm text-gray-600">
                  {getAmericanFinalBracketLabel(
                    tournament.americanFinalBracketSize,
                    {
                      teamCount: tournament.teams?.length,
                      hasFixture: tournament.hasFixture,
                    },
                  )}
                </p>
              </div>
            </div>
          )}
        </div>

        {showOverflowRow && (
          <div className="mt-4 flex items-start gap-3">
            <ListChecks className="mt-0.5 h-5 w-5 shrink-0 text-gray-500" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="font-medium">Grupos de 4 y clasificados</p>
                {showOverflowPencil && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onEditOverflowGroupClassificationClick}
                    className="h-6 w-6"
                    aria-label="Editar regla de clasificados en grupos de 4"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <p className="mt-1 text-sm text-gray-600">
                {isOverflowExtra
                  ? 'Opción activa: si el fixture forma grupos de 4 equipos, clasifican 3 por grupo.'
                  : 'Regla estándar: 2 clasificados por grupo (válido para grupos de 3 y de 4).'}
              </p>
              <p className="mt-1 text-xs text-gray-500">
                Solo cuando el formato es Corto y la estructura incluye fase de
                grupos.
              </p>
              {hasFixture && (
                <p className="mt-1 text-xs text-muted-foreground">
                  No editable: ya hay fixture generado (fase de grupos o
                  eliminatorias).
                </p>
              )}
            </div>
          </div>
        )}

        {isAmerican && showGroupsConfig && (
          <div className="mt-4 flex items-start gap-3">
            <ListChecks className="mt-0.5 h-5 w-5 shrink-0 text-gray-500" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="font-medium">Formato de partidos en el grupo</p>
                {showAmericanGroupFormatPencil && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onEditAmericanGroupFormatClick}
                    className="h-6 w-6"
                    aria-label="Editar formato de partidos en el grupo"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <p className="mt-1 text-sm text-gray-600">
                {getAmericanGroupFormatLabel(tournament)}
              </p>
              {hasFixture && (
                <p className="mt-1 text-xs text-muted-foreground">
                  No editable: ya hay fixture generado.
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {clubs.length > 0 && (
        <>
          <Separator />
          <div className="flex items-start gap-3">
            <MapPinIcon className="mt-0.5 h-5 w-5 text-gray-500" />
            <div className="min-w-0 flex-1">
              <p className="font-medium">Sede</p>
              <ul className="mt-2 space-y-3 text-sm text-gray-700">
                {clubs.map((club) => {
                  const courts = getCourtsForSport(club, sportUid);
                  const n = courts.length;
                  const scheduleBlocks =
                    tournament.schedule?.filter(
                      (entry) => entry.club.id === club.id,
                    ) ?? [];
                  return (
                    <li key={club.id}>
                      <p>
                        <span className="font-semibold text-gray-900">
                          {club.name}
                        </span>
                        <span className="text-gray-600">
                          {' '}
                          · {n} {n === 1 ? 'cancha' : 'canchas'}
                        </span>
                      </p>
                      {scheduleBlocks.length > 0 ? (
                        <ul className="mt-2 space-y-1 border-l border-gray-200 pl-3">
                          {scheduleBlocks.map((block, idx) => {
                            const name = scheduleBlockDisplayName(block, idx);
                            const range = scheduleBlockDateRangeLabel(block);
                            return (
                              <li
                                key={block.id ?? `${club.id}-schedule-${idx}`}
                                className="text-sm"
                              >
                                <span className="font-medium text-gray-900">
                                  {name}
                                </span>
                                {range ? (
                                  <span className="text-gray-600">
                                    {' · '}
                                    {range}
                                  </span>
                                ) : null}
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <p className="mt-1 text-xs text-gray-500">
                          Sin horarios asociados.
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </>
      )}
    </CollapsibleCard>
  );
}
